/**
 * OaSIS Explorer Frontend - Navigation Component
 * Task 30.10: Navbar Redesign
 * Task 32.6: Version-aware navigation links
 * 
 * Main navigation with responsive design and light/dark mode support
 * Height: 56px (h-14)
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, Search, Layers, BarChart3, Info, LogOut, ChevronDown, Bookmark, User } from 'lucide-react';
import { useTranslation, useAuth } from '../../contexts';
import { useSearchUsage } from '../../hooks/useSearchUsage';
import { useDatasetVersion } from '../../hooks';
import { ThemeToggle } from './ThemeToggle';
import { VersionSelector } from '../VersionSelector';
import { TrialBadge } from '../common';
import type { NavigationItem } from '../../types';

/**
 * Main navigation component
 * Supports light and dark modes with proper color transitions
 */
export const Navigation: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const location = useLocation();
  const t = useTranslation();
  const { user, loading, signOut } = useAuth();
  const { plan, isTrialing, trialDaysRemaining } = useSearchUsage();
  
  // Task 32.6: Get current version for versioned routes
  const { version } = useDatasetVersion();
  
  // useDatasetVersion resolves the active dataset for both versioned routes
  // and the homepage dataset override.
  const navVersion = version;
  const getVersionedPath = (segment: 'search' | 'classification' | 'sct') =>
    navVersion ? `/${navVersion}/${segment}` : `/${segment}`;

  // Check if user is Pro (includes trialing)
  const isPro = plan === 'pro';
  const currentPlanLabel = isTrialing ? 'Trial' : isPro ? 'Pro' : t('profile.freePlan');

  // Navigation items with icons
  // Task 32.6: Version-prefixed paths for data pages
  const navigationItems: NavigationItem[] = [
    {
      label: t('nav.home'),
      path: '/',
      icon: Home,
    },
    {
      label: t('nav.occupations'),
      path: getVersionedPath('search'),
      icon: Search,
    },
    {
      label: t('nav.classification'),
      path: getVersionedPath('classification'),
      icon: Layers,
    },
    {
      label: t('nav.sct'),
      path: getVersionedPath('sct'),
      icon: BarChart3,
    },
    {
      label: t('nav.about'),
      path: '/about',
      icon: Info,
    },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Task 32.6: Updated to handle versioned routes
  const isActiveRoute = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // For versioned routes, check if the current path matches the route type
    // e.g., /2023/search should match /:version/search
    const pathSegments = path.split('/').filter(Boolean);
    const locationSegments = location.pathname.split('/').filter(Boolean);
    
    // Check if this is a versioned path (/:version/:routeType)
    if (pathSegments.length >= 2) {
      // Compare the route type (second segment)
      const routeType = pathSegments[1]; // e.g., 'search', 'classification', 'sct'
      const locationRouteType = locationSegments[1];
      const locationHasVersionPrefix = locationSegments.length >= 2;
      
      // Match if location is also versioned and has same route type
      if (locationHasVersionPrefix && routeType === locationRouteType) {
        return true;
      }
    }
    
    return location.pathname.startsWith(path);
  };

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
    closeMobileMenu();
  };

  // User menu component for desktop
  const renderUserMenu = () => {
    if (loading) {
      return (
        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
      );
    }

    if (user) {
      return (
        <div className="relative flex items-center gap-3">
          {/* Trial badge - shown for active trials */}
          {isTrialing && trialDaysRemaining !== null ? (
            <TrialBadge daysRemaining={trialDaysRemaining} size="sm" />
          ) : null}
          
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="User menu"
          >
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
              {(user.displayName || user.email || 'U')[0].toUpperCase()}
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {userMenuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setUserMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-40 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col">
                    <p className="text-sm font-medium leading-none text-gray-900 dark:text-white truncate">
                      {user.displayName || 'User'}
                    </p>
                    <p className="text-xs leading-none text-gray-500 dark:text-gray-400 truncate">
                      {user.email}
                    </p>
                    <p className="text-xs leading-none text-gray-500 dark:text-gray-400">
                      {t('profile.currentPlan')}: {currentPlanLabel}
                    </p>
                  </div>
                </div>
                <div className="py-1">
                  <Link
                    to="/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-white transition-colors"
                  >
                    <User className="w-4 h-4" />
                    {t('nav.profile')}
                  </Link>
                  <Link
                    to="/user"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-white transition-colors"
                  >
                    <Bookmark className="w-4 h-4" />
                    {t('nav.savedSearches')}
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-white transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('auth.signOut')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      );
    }

    // Guest state: Sign In (text link) + Sign Up (primary button)
    return (
      <div className="flex items-center gap-3">
        <Link
          to="/signin"
          className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          {t('auth.signIn')}
        </Link>
        <Link
          to="/signup"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {t('auth.signUp')}
        </Link>
      </div>
    );
  };

  // Mobile user section
  const renderMobileUserSection = () => {
    if (loading) {
      return (
        <div className="px-3 py-2">
          <div className="w-full h-10 rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse" />
        </div>
      );
    }

    if (user) {
      return (
        <>
          <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 mt-1">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                {(user.displayName || user.email || 'U')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user.displayName || 'User'}
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                  {user.email}
                </p>
              </div>
              {isTrialing && trialDaysRemaining !== null ? (
                <TrialBadge daysRemaining={trialDaysRemaining} size="sm" />
              ) : null}
            </div>
          </div>
          <Link
            to="/profile"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-base font-medium transition-colors ${
              isActiveRoute('/profile')
                ? 'bg-blue-500/20 text-blue-400'
                : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            onClick={closeMobileMenu}
          >
            <User className="w-5 h-5" />
            <span>{t('nav.profile')}</span>
          </Link>
          <Link
            to="/user"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-base font-medium transition-colors ${
              isActiveRoute('/user')
                ? 'bg-blue-500/20 text-blue-400'
                : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            onClick={closeMobileMenu}
          >
            <Bookmark className="w-5 h-5" />
            <span>{t('nav.savedSearches')}</span>
          </Link>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 w-full transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>{t('auth.signOut')}</span>
          </button>
        </>
      );
    }

    // Guest mobile state
    return (
      <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700 mt-2 flex flex-col gap-2">
        <Link
          to="/signin"
          className="flex items-center justify-center py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          onClick={closeMobileMenu}
        >
          {t('auth.signIn')}
        </Link>
        <Link
          to="/signup"
          className="flex items-center justify-center py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
          onClick={closeMobileMenu}
        >
          {t('auth.signUp')}
        </Link>
      </div>
    );
  };

  return (
    <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          {/* Logo and brand */}
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white hover:opacity-90 transition-opacity"
              onClick={closeMobileMenu}
            >
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">O</span>
              </div>
              <span>{t('app.title')}</span>
            </Link>
          </div>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navigationItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = isActiveRoute(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900'
                      : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {IconComponent && <IconComponent className="w-4 h-4" />}
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Theme toggle, version selector, and user menu */}
          <div className="flex items-center gap-2">
            {/* Task 32.6: Version selector for versioned routes */}
            <VersionSelector compact className="hidden md:flex" />
            
            <ThemeToggle />
            
            {/* User menu (desktop) */}
            <div className="hidden md:block ml-2">
              {renderUserMenu()}
            </div>
            
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                type="button"
                onClick={toggleMobileMenu}
                aria-label={t('a11y.mainMenu')}
                className="inline-flex items-center justify-center p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile navigation menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700 py-2 bg-white dark:bg-gray-800">
            <div className="flex flex-col gap-1">
              {/* Task 32.6: Version selector for mobile */}
              <div className="px-3 py-2">
                <VersionSelector />
              </div>
              
              {navigationItems.map((item) => {
                const IconComponent = item.icon;
                const isActive = isActiveRoute(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-base font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    onClick={closeMobileMenu}
                  >
                    {IconComponent && <IconComponent className="w-5 h-5" />}
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              {/* Mobile user section */}
              {renderMobileUserSection()}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
